import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import { useGenerateIndividualDescription } from '@/api/hooks/useGenerateIndividualDescription'
import { useGenerateServiceGroupRecommendations } from '@/api/hooks/useGenerateServiceGroupRecommendations'
import { useServiceGroups } from '@/api/hooks/useServiceGroups'
import { Button } from '@/components/ui/button'
import { useFirebaseUser } from '@/hooks/useFirebaseUser'
import { useIndividuals } from '@/hooks/useIndividuals'
import { resetAllFirstTimeSeen } from '@/hooks/useFirstTimeSeen'
import PageHeader from '@/components/molecules/page-header'
import { ThemeToggle } from '@/components/theme-toggle'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/_authenticated/(developer)/debug')({
  component: DebugPage,
})

function DebugPage() {
  const { reloadUser } = useFirebaseUser()
  const { selectedIndividualId } = useIndividuals()
  const { isPending, mutateAsync: generateIndividualDescription } =
    useGenerateIndividualDescription()
  const { data: serviceGroups = [] } = useServiceGroups(selectedIndividualId)
  const { t } = useTranslation()
  const {
    isPending: isGeneratingRecommendations,
    mutateAsync: generateServiceGroupRecommendations,
  } = useGenerateServiceGroupRecommendations()
  const queryClient = useQueryClient()

  return (
    <div className="flex flex-col gap-4">
      <div className="my-4 flex flex-col gap-8 [&_>_div]:space-y-2">
        <PageHeader
          body={t('settingsPage.changeTheme.description')}
          heading={t('settingsPage.changeTheme.title')}
        >
          <ThemeToggle />
        </PageHeader>
        <div>
          <Button onClick={resetAllFirstTimeSeen} size="lg">
            Clear coach flags
          </Button>
          <p>Clears all coach flags, resetting first-time seen popovers.</p>
        </div>
        <div>
          <Button onClick={reloadUser} size="lg">
            Reload user
          </Button>
          <p>Reloads user claims and tokens, may fix auth issues.</p>
        </div>
        <div>
          <Button onClick={() => queryClient.invalidateQueries()} size="lg">
            Reload data
          </Button>
          <p>Reloads all queries in the React Query client, may fix stale data issues.</p>
        </div>
        <div>
          <Button
            isLoading={isPending}
            onClick={() => generateIndividualDescription({ individualId: selectedIndividualId! })}
            size="lg"
          >
            Generate individual description
          </Button>
          <p>Generates a description for the selected individual based on documents and notes.</p>
        </div>
        {serviceGroups.map(sg => (
          <div key={sg.id}>
            <Button
              isLoading={isGeneratingRecommendations}
              onClick={() =>
                generateServiceGroupRecommendations({
                  individualId: selectedIndividualId!,
                  serviceGroupId: sg.id,
                })
              }
              size="lg"
            >
              Generate {sg.title} recommendations
            </Button>
            <p>
              Generates recommendations for {sg.title} service group based on documents and notes.
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}
